<script setup>
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";
import ObjectiveDetailsCell from "@frontJs/components/tables/cells/projectManagement/tasks/tenantReports/ObjectiveDetailsCell.vue";

const { ongoing, done, perMonth = true, perQuarter = false } = defineProps({
  ongoing: {
    type: Boolean,
    required: false,
  },
  done: {
    type: Boolean,
    required: false,
  },
  perMonth: {
    type: Boolean,
    required: false,
  },
  perQuarter: {
    type: Boolean,
    required: false,
  }
});

const { t } = useI18n();

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.name,
          href:
            route("front.projectManagement.customer.updateCustomer", {
              id: props.row.original.id,
            }) + "?tab=apikomObjectives",
          target: "_self",
          inertia: true,
        }),
    }),
    columnHelper.display({
      id: "details",
      header: t("label.strategy"),
      cell: (innerProps) =>
        h(ObjectiveDetailsCell, {
          row: innerProps.row,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "numberOfStandardContentsPerMonth",
      header: () => t("label.standardContents"),
      cell: (props) =>
        props.row.original.numberOfStandardContentsPerMonth
          ? h(TextCellV2, {
              text:
                props.row.original.numberOfStandardContentsPerMonthDone +
                "/" +
                props.row.original.numberOfStandardContentsPerMonth,
              color:
                props.row.original.numberOfStandardContentsPerMonthDone >=
                props.row.original.numberOfStandardContentsPerMonth
                  ? "green"
                  : "red",
            })
          : h("div"),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "numberOfPremiumContentsPerMonth",
      header: () => t("label.premiumContents"),
      cell: (props) =>
        props.row.original.numberOfPremiumContentsPerMonth
          ? h(TextCellV2, {
              text:
                props.row.original.numberOfPremiumContentsPerMonthDone +
                "/" +
                props.row.original.numberOfPremiumContentsPerMonth,
              color:
                props.row.original.numberOfPremiumContentsPerMonthDone >=
                props.row.original.numberOfPremiumContentsPerMonth
                  ? "green"
                  : "red",
            })
          : h("div"),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "numberOfInternalMeshPagesPerMonth",
      header: () => t("label.internalMeshPages"),
      cell: (props) =>
        props.row.original.numberOfInternalMeshPagesPerMonth
          ? h(TextCellV2, {
              text:
                props.row.original.numberOfInternalMeshPagesPerMonthDone +
                "/" +
                props.row.original.numberOfInternalMeshPagesPerMonth,
              color:
                props.row.original.numberOfInternalMeshPagesPerMonthDone >=
                props.row.original.numberOfInternalMeshPagesPerMonth
                  ? "green"
                  : "red",
            })
          : h("div"),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "numberOfBannersPerMonth",
      header: () => t("label.banners"),
      cell: (props) =>
        props.row.original.numberOfBannersPerMonth
          ? h(TextCellV2, {
              text:
                props.row.original.numberOfBannersPerMonthDone +
                "/" +
                props.row.original.numberOfBannersPerMonth,
              color:
                props.row.original.numberOfBannersPerMonthDone >=
                props.row.original.numberOfBannersPerMonth
                  ? "green"
                  : "red",
            })
          : h("div"),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "numberOfEmailingPerMonth",
      header: () => t("label.emailing"),
      cell: (props) =>
        props.row.original.numberOfEmailingPerMonth
          ? h(TextCellV2, {
              text:
                props.row.original.numberOfEmailingPerMonthDone +
                "/" +
                props.row.original.numberOfEmailingPerMonth,
              color:
                props.row.original.numberOfEmailingPerMonthDone >=
                props.row.original.numberOfEmailingPerMonth
                  ? "green"
                  : "red",
            })
          : h("div"),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "numberOfUserTestsPerQuarter",
      header: () => t("label.userTests"),
      cell: (props) =>
        props.row.original.numberOfUserTestsPerQuarter
          ? h(TextCellV2, {
              text:
                props.row.original.numberOfUserTestsPerQuarterDone +
                "/" +
                props.row.original.numberOfUserTestsPerQuarter,
              color:
                props.row.original.numberOfUserTestsPerQuarterDone >=
                props.row.original.numberOfUserTestsPerQuarter
                  ? "green"
                  : "red",
            })
          : h("div"),
      enableSorting: false,
    }),
  ],
  url: route("front.projectManagement.task.objectives"),
  data: {
    ongoing,
    done,
    perMonth,
    perQuarter
  },
  columnVisibility: {
    numberOfStandardContentsPerMonth: perMonth,
    numberOfPremiumContentsPerMonth: perMonth,
    numberOfInternalMeshPagesPerMonth: perMonth,
    numberOfBannersPerMonth: perMonth,
    numberOfEmailingPerMonth: perMonth,
    numberOfUserTestsPerQuarter: perQuarter,
  }
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :set-global-filter="setGlobalFilter"
    :available-filters="availableFilters"
    :filters="filters"
    :set-filters="setFilters"
    wrap-headers
  />
</template>
